/**
 * Loader configuration, ported from the previous app's loader-constants.js with
 * the same values. The Cesium ion token moved to VITE_CESIUM_ION_TOKEN and the
 * demo-mode gates moved to the settings store.
 */

export const LOADER_API_BASE = '/api/loader';
export const SYNC_INTERVAL_MS = 30_000;

export const CELESTRAK = {
  BASE_URL: 'https://celestrak.org/NORAD/elements/gp.php',
  GROUPS: {
    'Space Stations': 'stations',
    'Active Satellites': 'active',
    Weather: 'weather',
    NOAA: 'noaa',
    GOES: 'goes',
    'GPS Operational': 'gps-ops',
    GLONASS: 'glo-ops',
    Galileo: 'galileo',
    Starlink: 'starlink',
    OneWeb: 'oneweb',
    'Iridium NEXT': 'iridium-NEXT',
    Globalstar: 'globalstar',
    'Amateur Radio': 'amateur',
    Science: 'science',
    Geodetic: 'geodetic',
    Military: 'military',
    TDRSS: 'tdrss',
    ARGOS: 'argos',
    Planet: 'planet',
    Spire: 'spire',
    GEO: 'geo',
    Cubesats: 'cubesat',
    'Last 30 Days': 'last-30-days',
    'Visual (Brightest)': 'visual',
  } as Record<string, string>,
  REFRESH_INTERVAL_MS: 7_200_000,
  MAX_CATALOG_FETCH: 10_000,
};

export interface WeatherLayerDef {
  url: string;
  layers: string;
  name: string;
  group: string;
  gibs: boolean;
  format?: string;
}

const GIBS = 'https://gibs.earthdata.nasa.gov/wms/epsg4326/best/wms.cgi';

export const WEATHER = {
  IEM_BASE: 'https://mesonet.agron.iastate.edu/cgi-bin/wms/',
  GIBS_BASE: GIBS,
  LAYERS: {
    viirs_true: {
      url: GIBS,
      layers: 'VIIRS_NOAA20_CorrectedReflectance_TrueColor',
      name: 'VIIRS True Color (Global)',
      group: 'Global Imagery',
      gibs: true,
      format: 'image/jpeg',
    },
    modis_terra: {
      url: GIBS,
      layers: 'MODIS_Terra_CorrectedReflectance_TrueColor',
      name: 'MODIS Terra (Global)',
      group: 'Global Imagery',
      gibs: true,
      format: 'image/jpeg',
    },
    modis_aqua: {
      url: GIBS,
      layers: 'MODIS_Aqua_CorrectedReflectance_TrueColor',
      name: 'MODIS Aqua (Global)',
      group: 'Global Imagery',
      gibs: true,
      format: 'image/jpeg',
    },
    cloud_top_temp: {
      url: GIBS,
      layers: 'MODIS_Terra_Cloud_Top_Temp_Day',
      name: 'Cloud Top Temperature (Global)',
      group: 'Clouds and Atmosphere',
      gibs: true,
      format: 'image/png',
    },
    water_vapor: {
      url: GIBS,
      layers: 'MODIS_Terra_Water_Vapor_5km_Day',
      name: 'Water Vapor (Global)',
      group: 'Clouds and Atmosphere',
      gibs: true,
      format: 'image/png',
    },
    aerosol: {
      url: GIBS,
      layers: 'MODIS_Combined_Value_Added_AOD',
      name: 'Aerosol, Dust, Smoke (Global)',
      group: 'Clouds and Atmosphere',
      gibs: true,
      format: 'image/png',
    },
    fire_viirs: {
      url: GIBS,
      layers: 'VIIRS_NOAA20_Thermal_Anomalies_375m_All',
      name: 'Active Wildfires, VIIRS (Global)',
      group: 'Wildfires and Thermal',
      gibs: true,
      format: 'image/png',
    },
    fire_modis: {
      url: GIBS,
      layers: 'MODIS_Terra_Thermal_Anomalies_All',
      name: 'Active Wildfires, MODIS (Global)',
      group: 'Wildfires and Thermal',
      gibs: true,
      format: 'image/png',
    },
    sst: {
      url: GIBS,
      layers: 'GHRSST_L4_MUR_Sea_Surface_Temperature',
      name: 'Sea Surface Temperature (Global)',
      group: 'Ocean',
      gibs: true,
      format: 'image/png',
    },
    chlorophyll: {
      url: GIBS,
      layers: 'MODIS_Aqua_L2_Chlorophyll_A',
      name: 'Ocean Chlorophyll (Global)',
      group: 'Ocean',
      gibs: true,
      format: 'image/png',
    },
    snow_cover: {
      url: GIBS,
      layers: 'MODIS_Terra_NDSI_Snow_Cover',
      name: 'Snow Cover (Global)',
      group: 'Snow, Ice and Land',
      gibs: true,
      format: 'image/png',
    },
    sea_ice: {
      url: GIBS,
      layers: 'MODIS_Aqua_Sea_Ice',
      name: 'Sea Ice Extent (Polar)',
      group: 'Snow, Ice and Land',
      gibs: true,
      format: 'image/png',
    },
    land_surface: {
      url: GIBS,
      layers: 'MODIS_Terra_Land_Surface_Temp_Day',
      name: 'Land Surface Temperature (Global)',
      group: 'Snow, Ice and Land',
      gibs: true,
      format: 'image/png',
    },
    nightlights: {
      url: GIBS,
      layers: 'VIIRS_SNPP_DayNightBand_At_Sensor_Radiance',
      name: 'Night Lights, VIIRS (Global)',
      group: 'Night Lights',
      gibs: true,
      format: 'image/png',
    },
    goes_ir: {
      url: 'https://mesonet.agron.iastate.edu/cgi-bin/wms/goes/conus_ir.cgi',
      layers: 'goes_conus_ir',
      name: 'GOES East Infrared (US)',
      group: 'US Real-Time',
      gibs: false,
    },
    goes_vis: {
      url: 'https://mesonet.agron.iastate.edu/cgi-bin/wms/goes/conus_vis.cgi',
      layers: 'goes_conus_vis',
      name: 'GOES East Visible (US)',
      group: 'US Real-Time',
      gibs: false,
    },
    goes_wv: {
      url: 'https://mesonet.agron.iastate.edu/cgi-bin/wms/goes/conus_wv.cgi',
      layers: 'goes_conus_wv',
      name: 'GOES Water Vapor (US)',
      group: 'US Real-Time',
      gibs: false,
    },
    nexrad: {
      url: 'https://mesonet.agron.iastate.edu/cgi-bin/wms/nexrad/n0r.cgi',
      layers: 'nexrad-n0r-900913',
      name: 'NEXRAD Radar (US)',
      group: 'US Real-Time',
      gibs: false,
    },
  } as Record<string, WeatherLayerDef>,
  REFRESH_INTERVAL_MS: 600_000,
  IMPACT_CACHE_MS: 1_800_000,
};

export const CESIUM_CONFIG = {
  /** Esri World Dark Gray Canvas, keyless raster; the template is {z}/{y}/{x}. */
  DARK_TILE_URL:
    'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}',
  DARK_TILE_MAX_LEVEL: 16,
  GOOGLE_TILE_URL: 'https://mt1.google.com/vt/lyrs={lyrs}&x={x}&y={y}&z={z}',
  INITIAL_CAMERA: { lon: -95, lat: 0, height: 25_000_000 },
  CLOCK_MULTIPLIER: 60,
  ORBIT_STEP_SECONDS: 60,
  CONTACT_CHECK_SECONDS: 30,
  /** Fraction of the orbital period drawn ahead of each satellite. */
  ORBIT_PATH_LEAD_FRACTION: 0.25,
};

export const TIMEFRAME = {
  DEFAULT_HOURS: 24,
  DEFAULT_FY: 2026,
  /** Federal fiscal year starts in October. */
  FY_START_MONTH: 10,
  OPTIONS_HOURS: [6, 24, 168, 720, 4380, 8760],
  FISCAL_YEARS: [2025, 2026, 2027, 2028],
};

/** WGS84 reference ellipsoid (NIMA TR8350.2). */
export const EARTH = {
  WGS84_A_KM: 6378.137,
  WGS84_B_KM: 6356.7523142,
  WGS84_F: 1 / 298.257223563,
  WGS84_E2: 0.00669437999014,
  VOLUMETRIC_MEAN_RADIUS_KM: 6371.0088,
  /** km^3/s^2 */
  GM: 398600.4418,
};

export const ANALYSIS = {
  /** Antennas are never scheduled 24 h a day (client guidance: no more than 18). */
  MAX_HOURS_PER_DAY: 18,
  MIN_ELEVATION_DEG: 5,
  SLA_MET_THRESHOLD: 95,
  SLA_PARTIAL_THRESHOLD: 70,
  CONFLICT_TIME_WINDOW_MS: 7_200_000,
  LARGE_NETWORK_THRESHOLD: 200,
  DEFAULT_TRACKED_SUBSET: 100,
  ANALYSIS_SOFT_CAP: 500,
};

export interface AltitudeBand {
  id: string;
  label: string;
  minAltKm: number;
  maxAltKm: number;
  representativeAltKm: number;
}

export const ALTITUDE_BANDS: AltitudeBand[] = [
  { id: 'leo_low', label: 'LEO-Low', minAltKm: 400, maxAltKm: 600, representativeAltKm: 550 },
  { id: 'leo_standard', label: 'LEO-Std', minAltKm: 600, maxAltKm: 900, representativeAltKm: 824 },
  { id: 'leo_high', label: 'LEO-High', minAltKm: 900, maxAltKm: 2000, representativeAltKm: 1336 },
];

export const RESOLUTION = {
  MOVE_TO_ALTERNATE: 'MOVE_TO_ALTERNATE',
  ACCEPT_REDUCED: 'ACCEPT_REDUCED',
  WAIT_NEXT_PASS: 'WAIT_NEXT_PASS',
  BUMP_LOWER: 'BUMP_LOWER',
  UNRESOLVED: 'UNRESOLVED',
} as const;
export type ResolutionStrategy = (typeof RESOLUTION)[keyof typeof RESOLUTION];

/** Visual constants the globe and panels share. */
export const VISUAL = {
  COVERAGE_FILL_ALPHA: 0.08,
  COVERAGE_OUTLINE_ALPHA: 0.4,
  COVERAGE_RING_ALPHA: 0.3,
  COVERAGE_RING_DASH: 16,
  COVERAGE_RING_WIDTH: 1.5,
  CONTACT_LINE_ALPHA: 0.7,
  CONTACT_GLOW: 0.3,
  /** TLE age thresholds for the satellite outline (days). */
  TLE_WARN_DAYS: 7,
  TLE_STALE_DAYS: 14,
};

/** Guam coverage-gap demo. */
export const GUAM = {
  STATION_ID: 'guam_13m',
  STATION: {
    id: 'guam_13m',
    name: 'Guam Ground Station',
    shortName: 'GUM-13',
    network: 'Guam',
    location: { lat: 13.44, lon: 144.79, name: 'Dededo, Guam', country: 'USA' },
    capabilities: {
      diameterM: 13,
      bands: ['S-band', 'X-band'],
      maxHoursPerDay: 18,
      maintenanceWindowHrs: 6,
      uplinkCapable: true,
      type: 'Parabolic Dish',
    },
    coverage: { effectiveRadiusKm: 3500, elevationMinDeg: 5 },
    performance: { averageSuccessRate: 0.94, weatherImpactFactor: 0.08, currentUtilization: 0 },
    color: '#8BC34A',
    icon: 'fas fa-satellite-dish',
    enabled: true,
  },
  GAP: {
    name: 'Indian Ocean Gap',
    centerLat: -10,
    centerLon: 75,
    radiusKm: 4000,
    severity: 'critical' as const,
  },
  CAMERA_TARGET: { lon: 70, lat: -5, height: 15_000_000 },
  BLACKOUT_MINUTES: 47,
  BLACKOUT_WITH_GUAM: 12,
};

/** JPL Horizons bodies offered in the deep-space picker. */
export const HORIZONS_BODIES: { key: string; id: string; label: string }[] = [
  { key: 'jwst', id: '-170', label: 'JWST (L2)' },
  { key: 'tess', id: '-95', label: 'TESS (GEO+)' },
  { key: 'voyager1', id: '-31', label: 'Voyager 1' },
  { key: 'voyager2', id: '-32', label: 'Voyager 2' },
  { key: 'juice', id: '-28', label: 'JUICE' },
  { key: 'europa_clipper', id: '-159', label: 'Europa Clipper' },
  { key: 'new_horizons', id: '-98', label: 'New Horizons' },
  { key: 'psyche', id: '-255', label: 'Psyche' },
  { key: 'lucy', id: '-49', label: 'Lucy' },
  { key: 'parker', id: '-96', label: 'Parker Solar Probe' },
  { key: 'soho', id: '-21', label: 'SOHO (L1)' },
  { key: 'gaia', id: '-139479', label: 'Gaia (L2)' },
];

/** Constellation overlays (not analysed), fetched through the CelesTrak proxy. */
export const OVERLAYS: {
  key: string;
  label: string;
  group: string;
  supplemental?: boolean;
  filter?: RegExp;
  color: string;
}[] = [
  { key: 'starlink', label: 'Starlink', group: 'starlink-sup', supplemental: true, color: '#00bcd4' },
  { key: 'oneweb', label: 'OneWeb', group: 'oneweb', color: '#9c27b0' },
  { key: 'stations', label: 'ISS and Tiangong', group: 'stations', color: '#FFC107' },
  { key: 'gnss_gps', label: 'GPS', group: 'gps-ops', color: '#4caf50' },
  { key: 'gnss_glonass', label: 'GLONASS', group: 'glo-ops', color: '#8bc34a' },
  { key: 'gnss_galileo', label: 'Galileo', group: 'galileo', color: '#cddc39' },
  { key: 'gnss_beidou', label: 'BeiDou', group: 'beidou', color: '#ffeb3b' },
  { key: 'sentinel', label: 'Sentinel', group: 'active', filter: /^SENTINEL-/i, color: '#2196F3' },
  { key: 'satnogs', label: 'Cubesats (SatNOGS)', group: 'cubesat', color: '#FF9800' },
];
